import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import { Check, CopySimple, PencilSimple, Plus, X } from "@phosphor-icons/react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useBreakpoint } from "@stylingcv/hooks";
import type { SectionItem, SectionWithItem } from "@stylingcv/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  MobileDialogContent,
  ScrollArea,
} from "@stylingcv/ui";
import { produce } from "immer";
import get from "lodash.get";
import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

import type { DialogName } from "@/client/stores/dialog";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

const FORM_SECTION_ORDER: readonly string[] = ["basics", "summary"];

const SECTION_LABELS: Record<string, string> = {
  basics: "SECTION 01 · ABOUT YOU",
  summary: "SECTION 02 · YOUR PITCH",
  experience: "SECTION 03 · WORK HISTORY",
  education: "SECTION 04 · EDUCATION",
  skills: "SECTION 05 · SKILLS",
  languages: "SECTION 06 · LANGUAGES",
  projects: "SECTION 07 · PROJECTS",
  certifications: "SECTION 08 · CERTIFICATIONS",
  awards: "SECTION 09 · AWARDS",
  publications: "SECTION 10 · PUBLICATIONS",
  volunteer: "SECTION 11 · VOLUNTEERING",
  interests: "SECTION 12 · INTERESTS",
  references: "SECTION 13 · REFERENCES",
};

const SECTION_TITLES: Record<string, string> = {
  basics: "Who you are",
  summary: "Your pitch",
  experience: "Work history",
  education: "Education",
  skills: "Skills",
  languages: "Languages",
  projects: "Projects",
  certifications: "Certifications",
  awards: "Awards",
  publications: "Publications",
  volunteer: "Volunteering",
  interests: "Interests",
  references: "References",
};

type Props<T extends SectionItem> = {
  id: DialogName;
  form: UseFormReturn<T>;
  defaultValues: T;
  pendingKeyword?: string;
  children: React.ReactNode;
};

export const SectionDialog = <T extends SectionItem>({
  id,
  form,
  defaultValues,
  pendingKeyword,
  children,
}: Props<T>) => {
  const { isOpen, mode, close, payload } = useDialog<T>(id);
  const { isMobile } = useBreakpoint();
  const [showDiscardAlert, setShowDiscardAlert] = useState(false);
  const pendingCloseRef = useRef(false);

  const setValue = useResumeStore((state) => state.setValue);
  const section = useResumeStore((state) => {
    return get(state.resume.data.sections, id);
  }) as SectionWithItem<T> | null;

  const isCreate = mode === "create";
  const isUpdate = mode === "update";
  const isDelete = mode === "delete";
  const isDuplicate = mode === "duplicate";

  useEffect(() => {
    if (isOpen) {
      pendingCloseRef.current = false;
      onReset();
    }
  }, [isOpen, payload]);

  const safeClose = useCallback(() => {
    if (form.formState.isDirty) {
      setShowDiscardAlert(true);
    } else {
      close();
    }
  }, [form.formState.isDirty, close]);

  const handleDiscard = useCallback(() => {
    setShowDiscardAlert(false);
    close();
  }, [close]);

  const handleKeepEditing = useCallback(() => {
    setShowDiscardAlert(false);
  }, []);

  const handleInteractOutside = useCallback(
    (e: Event) => {
      if (form.formState.isDirty) {
        e.preventDefault();
        setShowDiscardAlert(true);
      }
    },
    [form.formState.isDirty],
  );

  const handleEscapeKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (form.formState.isDirty) {
        e.preventDefault();
        setShowDiscardAlert(true);
      }
    },
    [form.formState.isDirty],
  );

  const onSubmit = (values: T) => {
    if (!section) return;

    if (isCreate || isDuplicate) {
      if (pendingKeyword && "keywords" in values) {
        values.keywords.push(pendingKeyword);
      }

      setValue(
        `sections.${id}.items`,
        produce(section.items, (draft: T[]): void => {
          draft.push({ ...values, id: createId() });
        }),
      );
    }

    if (isUpdate) {
      if (!payload.item?.id) return;

      if (pendingKeyword && "keywords" in values) {
        values.keywords.push(pendingKeyword);
      }

      setValue(
        `sections.${id}.items`,
        produce(section.items, (draft: T[]): void => {
          const index = draft.findIndex((item) => item.id === payload.item?.id);
          if (index === -1) return;
          draft[index] = values;
        }),
      );
    }

    if (isDelete) {
      if (!payload.item?.id) return;

      setValue(
        `sections.${id}.items`,
        produce(section.items, (draft: T[]): void => {
          const index = draft.findIndex((item) => item.id === payload.item?.id);
          if (index === -1) return;
          draft.splice(index, 1);
        }),
      );
    }

    close();
  };

  const onReset = () => {
    if (isCreate) form.reset({ ...defaultValues, id: createId() } as T);
    if (isUpdate) form.reset({ ...defaultValues, ...payload.item });
    if (isDuplicate) form.reset({ ...payload.item, id: createId() } as T);
    if (isDelete) form.reset({ ...defaultValues, ...payload.item });
  };

  if (isDelete) {
    return (
      <AlertDialog open={isOpen} onOpenChange={close}>
        <AlertDialogContent>
          <Form {...form}>
            <form>
              <AlertDialogHeader>
                <AlertDialogTitle>{t`Are you sure you want to delete this item?`}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t`This action can be reverted by clicking on the undo button in the floating toolbar.`}
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
                <AlertDialogAction variant="error" onClick={form.handleSubmit(onSubmit)}>
                  {t`Delete`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={safeClose}>
        {isMobile ? (
          /* ── Mobile: bottom sheet (unchanged layout) ── */
          <MobileDialogContent
            onClose={safeClose}
            onInteractOutside={handleInteractOutside}
            onEscapeKeyDown={handleEscapeKeyDown}
          >
            <Form {...form}>
              <ScrollArea>
                <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
                  {/* Section header */}
                  <div className="mb-4">
                    <p className="text-[8px] font-extrabold uppercase tracking-[0.12em] text-[#e8885e]">
                      {SECTION_LABELS[id as string] ?? (id as string).toUpperCase()}
                    </p>
                    <h2 className="mt-1 text-[17px] font-extrabold text-[#0A1628]">
                      {SECTION_TITLES[id as string] ?? (id as string)}
                    </h2>
                  </div>

                  {children}

                  {/* Sticky CTA */}
                  <div className="sticky bottom-0 -mx-6 mt-6 border-t border-black/[0.06] bg-[#faf9f5] px-6 pb-4 pt-3">
                    <button
                      type="submit"
                      className="w-full rounded-xl bg-[#0A1628] py-3 text-[10px] font-bold text-white"
                    >
                      {FORM_SECTION_ORDER.includes(id as string)
                        ? t`Continue →`
                        : t`Done – back to sections`}
                    </button>
                  </div>
                </form>
              </ScrollArea>
            </Form>
          </MobileDialogContent>
        ) : (
          /* ── Desktop: redesigned modal matching design handoff ── */
          <DialogContent
            onInteractOutside={handleInteractOutside}
            onEscapeKeyDown={handleEscapeKeyDown}
            className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-[760px] flex-col gap-0 overflow-hidden p-0 [&>button:last-child]:hidden"
          >
            <Form {...form}>
              <form
                className="flex min-h-0 flex-1 flex-col overflow-hidden"
                onSubmit={form.handleSubmit(onSubmit)}
                onKeyDown={(e: React.KeyboardEvent<HTMLFormElement>) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    e.preventDefault();
                    form.handleSubmit(onSubmit)();
                  }
                }}
              >
                {/* ── Header ── */}
                <div className="flex shrink-0 items-center gap-3.5 border-b border-gray-100 bg-gradient-to-b from-white to-[#FAFAFA] px-5 pb-3.5 pt-4">
                  <div className="min-w-0 flex-1">
                    <DialogTitle className="flex items-center gap-2.5 text-[22px] font-bold leading-tight tracking-[-0.01em] text-[#155469] [font-family:var(--font-serif,Georgia,serif)]">
                      {isCreate && <Plus size={18} className="shrink-0 text-[#E8885E]" />}
                      {isUpdate && <PencilSimple size={18} className="shrink-0 text-[#E8885E]" />}
                      {isDuplicate && <CopySimple size={18} className="shrink-0 text-[#E8885E]" />}
                      <span>
                        {isCreate && t`Create a new item`}
                        {isUpdate && t`Update an existing item`}
                        {isDuplicate && t`Duplicate an existing item`}
                      </span>
                    </DialogTitle>
                  </div>
                  <button
                    type="button"
                    onClick={safeClose}
                    className="flex size-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  >
                    <X size={16} />
                    <span className="sr-only">{t`Close`}</span>
                  </button>
                </div>

                {/* ── Scrollable body ── */}
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <VisuallyHidden>
                    <DialogDescription />
                  </VisuallyHidden>
                  <div className="px-[22px] py-5">
                    {children}
                  </div>
                </div>

                {/* ── Footer ── */}
                <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 bg-[#FAFAFA] px-[22px] py-[14px]">
                  {/* Keyboard shortcuts */}
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400">
                    <kbd className="rounded border border-b-2 border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-gray-500">
                      Esc
                    </kbd>
                    <span>{t`to cancel`}</span>
                    <span className="mx-0.5 text-gray-300">·</span>
                    <kbd className="rounded border border-b-2 border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-gray-500">
                      ⌘
                    </kbd>
                    <kbd className="rounded border border-b-2 border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-gray-500">
                      ↵
                    </kbd>
                    <span>{t`to save`}</span>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={safeClose}
                      className="cursor-pointer rounded-[9px] border border-gray-300 bg-white px-[18px] py-[9px] text-[13px] font-semibold text-gray-900 transition-colors hover:border-gray-400"
                    >
                      {t`Cancel`}
                    </button>
                    <button
                      type="submit"
                      className="flex cursor-pointer items-center gap-1.5 rounded-[9px] bg-gradient-to-br from-[#155469] via-[#268599] to-[#E8885E] px-[18px] py-[9px] text-[13px] font-bold text-white shadow-[0_4px_14px_rgba(38,133,153,0.30)] transition-opacity hover:opacity-90"
                    >
                      <Check size={13} weight="bold" />
                      <span>
                        {isCreate && t`Create`}
                        {isUpdate && t`Save Changes`}
                        {isDuplicate && t`Duplicate`}
                      </span>
                    </button>
                  </div>
                </div>
              </form>
            </Form>
          </DialogContent>
        )}
      </Dialog>

      <AlertDialog open={showDiscardAlert} onOpenChange={setShowDiscardAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Discard unsaved changes?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`You have unsaved changes that will be lost if you close this dialog.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleKeepEditing}>{t`Keep Editing`}</AlertDialogCancel>
            <AlertDialogAction variant="error" onClick={handleDiscard}>
              {t`Discard`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
